import React, { useContext, useEffect, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { FaHandPointRight, FaRegCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { initFacebookPixel, trackEvent } from '../../../../../pixel';
import { config } from '../../../../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { PixelContext } from '../../../../Context/PixelContext';

const apiUrl = config.apiUrl;

const RegistrationForm = ({leads, handleCloseForm, slug }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    email: '',
    whatsapp: '+880', // Set default to Bangladesh country code
    displayWhatsapp: '' // Separate state for display
  });

   // Helper function to get cookies
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  const [leadPage, setLeadPage] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [automation, setAutomation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Color scheme to match WebinarHome
  const gradientClass = 'bg-gradient-to-r from-blue-600 to-indigo-700';
  const bgColor = 'bg-blue-600';
  const textColor = 'text-blue-600';
  const bgShadow = 'bg-blue-50';
  const hoverColor = 'hover:bg-blue-700';

  const { pixel } = useContext(PixelContext);
  const pixelId = pixel?.pixel_id || null;

const pixelInitialized = useRef(false);

useEffect(() => {
  if (pixelId && !pixelInitialized.current) {
    initFacebookPixel(pixelId);
    trackEvent('InitiateCheckout');
    pixelInitialized.current = true;
  }
}, [pixelId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const leadResponse = await axios.get(`${apiUrl}/leads/${slug}`);
        setLeadPage(leadResponse.data.lead);

        const automationResponse = await axios.get(`${apiUrl}/automations/${slug}`);
        setAutomation(automationResponse.data.automation);

      } catch (error) {
        console.error('Error fetching data:', error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    };

    setFormData(prev => ({ ...prev, slug: slug }));
    fetchData();
  }, [slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'whatsapp') {
      // Only allow numbers and limit to 10 digits (excluding +880)
      const numbersOnly = value.replace(/[^0-9]/g, '');
      const limitedNumbers = numbersOnly.slice(0, 15);
      
      // Update display value (without country code)
      setFormData(prev => ({
        ...prev,
        displayWhatsapp: limitedNumbers,
        whatsapp: '+880' + limitedNumbers // Store full number with country code
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try{
      const eventId = 'evt_' + crypto.randomUUID(); 
      
      const eventData = {
        email: formData.email,
        phone: formData.whatsapp,
        name: formData.name,
        event: 'Lead',    
        content_type: 'webinar',
        event_source_url: window.location.href,
        fbc: getCookie('_fbc'),
        fbp: getCookie('_fbp'),
        event_id: eventId,
        external_id: formData.whatsapp
      };


      trackEvent('Lead', eventData);

      // Track server-side
      await axios.post(`${apiUrl}/fb-track`, {
        event: 'Lead',
        ...eventData
      });
    } catch (error) {
      console.error('Facebook tracking failed:', error);
    }

    if (loading) return;

    // Validate WhatsApp number (should have 10 digits after +880)
    if (!formData.whatsapp || formData.whatsapp.length !== 15) {
      setMessage('Please enter a valid 10-digit phone number');
      setIsSubmitting(false);
      return;
    }

    try {
      localStorage.setItem('name', formData.name);
      localStorage.setItem('email', formData.email);
      localStorage.setItem('whatsapp', formData.whatsapp);

      await axios.post(`${apiUrl}/applications`, formData);

      const brevoApiUrl = 'https://api.brevo.com/v3/contacts';
      const apiKey = automation.api;
      const listId = Number(automation.list);

      try {
        await axios.post(
          brevoApiUrl,
          {
            email: formData.email,
            listIds: [listId],
            updateEnabled: true,
            attributes: {
              FIRSTNAME: formData.name,
              WHATSAPP: formData.whatsapp,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'api-key': apiKey,
            },
          }
        );

        window.location.href = leadPage.redirectPage;
        
      } catch (error) {
        if (error.response) {
          if (error.response.data.code === 'duplicate_parameter') {
            window.location.href = leadPage.redirectPage;
          } else {
            setMessage(error.response.data.message || 'Error occurred.');
          }
        } else {
          setMessage('Error occurred.');
        }
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      setMessage('Error occurred while submitting the data.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="sticky inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={handleCloseForm}
        ></div>

        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className={`relative bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden ${bgShadow}`}
        >
          <div className={`${gradientClass} p-4 text-white`}>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {leads.leadHeadline || "Reserve Your Spot Now"}
              </h2>
              <button
                onClick={handleCloseForm}
                className="text-white hover:text-blue-200 transition-colors"
              >
                <IoClose className="text-2xl" />
              </button>
            </div>
            <p className="text-sm opacity-90 mt-1">
              {leads.leadButtonSubHeadline || "Limited seats available - Register today!"}
            </p>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    +880
                  </span>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.displayWhatsapp}
                    onChange={handleChange}
                    placeholder="1234567890"
                    required
                    className="mt-1 block w-full px-4 py-3 rounded-none rounded-r-lg border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="pt-2">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full ${gradientClass} text-white py-4 px-4 rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-2 text-lg">
                        <FaHandPointRight className="text-xl" />
                        <span>{leads.leadButtonHeadline || "Register Now"}</span>
                      </div>
                      <div className="text-sm font-normal mt-1 opacity-90">
                        {leads.leadButtonSubHeadline || "Get instant access"}
                      </div>
                    </div>
                  )}
                </motion.button>
              </div>

              {message && (
                <div className={`p-3 rounded-lg ${bgShadow} ${textColor} text-sm flex items-center gap-2`}>
                  <FaRegCheckCircle />
                  {message}
                </div>
              )}

              <div className={`text-xs ${textColor} text-center mt-4`}>
                <p>We respect your privacy. Unsubscribe at any time.</p>
              </div>
            </form>
          </div>

          <div className={`${bgShadow} p-4 text-center`}>
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="flex items-center">
                <span className={`inline-block w-2 h-2 rounded-full ${bgColor} mr-1`}></span>
                <span className={`inline-block w-2 h-2 rounded-full ${bgColor} mr-1`}></span>
                <span className={`inline-block w-2 h-2 rounded-full ${bgColor}`}></span>
              </div>
              <span className={`${textColor} font-medium`}>Secure Registration</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RegistrationForm;